import React from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";

const PLANS = [
  {
    key: "essentiel",
    name: "Essentiel",
    price: 29,
    originalPrice: 49,
    desc: "L'essentiel pour partir bien préparé",
    features: [
      "Itinéraire jour par jour, personnalisé",
      "Informations pratiques essentielles",
      "Checklist documents et visa",
      "Ma validation personnelle avant livraison",
    ],
    excluded: [
      "Budget détaillé par poste de dépense",
      "Recommandations restaurants & adresses locales",
      "Liens de réservation pré-sélectionnés",
      "Conseiller voyage dédié",
    ],
  },
  {
    key: "confort",
    name: "Confort",
    price: 49,
    originalPrice: 89,
    popular: true,
    desc: "Le plus complet pour voyager sereinement",
    features: [
      "Itinéraire jour par jour, enrichi",
      "Budget détaillé poste par poste (héberg., transport, resto…)",
      "Recommandations restaurants, activités & adresses locales",
      "Infos pratiques complètes + numéros d'urgence",
      "Checklist documents et visa",
      "Liens de réservation pré-sélectionnés",
      "Ma validation et relecture approfondies",
    ],
    excluded: ["Conseiller voyage dédié"],
  },
  {
    key: "serenite",
    name: "Sérénité",
    price: 89,
    originalPrice: 149,
    desc: "L'accompagnement expert de bout en bout",
    features: [
      "Tout l'offre Confort, sans exception",
      "Validation premium — je prends le temps qu'il faut",
      "Session questions/réponses avant le départ",
      "Conseiller voyage dédié, disponible à toute heure",
      "Je vous suis jusqu'à votre envol — et au-delà",
    ],
    excluded: [],
  },
];

export default function LandingPricing() {
  return (
    <section className="py-20 border-y border-[#DCD4C6]" style={{ background: "#F1EBE1" }}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14">
          <p className="section-label mb-4">Nos offres</p>
          <h2
            className="text-4xl sm:text-5xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
          >
            Choisissez votre niveau d'accompagnement
          </h2>
          <p className="text-sm text-[#5C554E] italic max-w-xl mx-auto" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Paiement unique par voyage · Accès permanent pendant toute la durée du voyage + 15 jours
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan) => (
            <div
              key={plan.key}
              className={`relative flex flex-col ${plan.popular ? "border-2 border-[#C17F59]" : "border border-[#DCD4C6]"}`}
              style={{ background: "#F9F6F0", borderRadius: "2px" }}
              data-testid={`plan-card-${plan.key}`}
            >
              {plan.popular && (
                <div
                  className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#C17F59] text-[#F9F6F0] text-xs px-4 py-1 whitespace-nowrap"
                  style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px", letterSpacing: "0.05em" }}
                >
                  Le plus populaire
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="mb-4">
                  <h3 className="text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
                    {plan.name}
                  </h3>
                  <p className="text-xs text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                    {plan.desc}
                  </p>
                </div>

                <div className="mb-6">
                  {plan.originalPrice && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl text-[#9A8F86] line-through" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                        {plan.originalPrice} €
                      </span>
                      <span
                        className="text-xs bg-[#C17F59] text-[#F9F6F0] px-2 py-0.5"
                        style={{ borderRadius: "2px", fontFamily: "'Lora', Georgia, serif", letterSpacing: "0.05em" }}
                      >
                        Lancement
                      </span>
                    </div>
                  )}
                  <span className="text-5xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#C17F59" }}>
                    {plan.price} €
                  </span>
                  <span className="text-xs text-[#9A8F86] ml-2 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>par voyage</span>
                </div>

                <ul className="space-y-2.5 flex-1 mb-6">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                      <Check size={13} className="text-[#C17F59] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                  {plan.excluded.map((f, i) => (
                    <li key={`ex-${i}`} className="flex items-start gap-2.5 text-sm text-[#C8C0B8]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                      <X size={13} className="text-[#C8C0B8] mt-0.5 flex-shrink-0" strokeWidth={2} />
                      {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  className={plan.popular ? "btn-primary text-center text-sm w-full" : "btn-secondary text-center text-sm w-full"}
                  data-testid={`plan-cta-${plan.key}`}
                >
                  Choisir cette offre
                </Link>
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm text-center text-[#8B5E34] font-medium mt-6 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          Tarif garanti pour les 50 prochaines commandes
        </p>
        <p className="text-xs text-[#9A8F86] mt-4 text-center italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          La création de votre dossier voyage est gratuite. Vous choisissez votre offre uniquement après ma validation.
        </p>
      </div>
    </section>
  );
}
